import {z} from 'zod';

import {isNil} from 'ramda';

import {
  NonNegativeBigDecimalSchema,
  NonNegativeMoneySchema,
  UuidResponseBody,
} from '@dms/api/shared';

// Enums verified against the sales_context OpenAPI spec
// (VehicleFinancingTemplateResponseBody / CreateVehicleFinancingTemplateRequestBody).
export const VehicleFinancingTemplateTypeSchema = z.union([
  z.literal('STANDARD'),
  z.literal('BALLOON'),
]);
export type VehicleFinancingTemplateType = z.infer<typeof VehicleFinancingTemplateTypeSchema>;

export const VehicleFinancingTemplateExpirationActionSchema = z.union([
  z.literal('REMOVE'),
  z.literal('REPLACE'),
]);
export type VehicleFinancingTemplateExpirationAction = z.infer<
  typeof VehicleFinancingTemplateExpirationActionSchema
>;

export const VehicleFinancingInterestRateTypeSchema = z.union([
  z.literal('FIXED'),
  z.literal('VARIABLE'),
  z.literal('COMBINED'),
]);
export type VehicleFinancingInterestRateType = z.infer<
  typeof VehicleFinancingInterestRateTypeSchema
>;

// Used for both `downPaymentStep` and `balloonPaymentStep`.
export const VehicleFinancingPaymentStepSchema = z.union([
  z.literal('ONE_PERCENT'),
  z.literal('FIVE_PERCENT'),
  z.literal('TEN_PERCENT'),
]);
export type VehicleFinancingPaymentStep = z.infer<typeof VehicleFinancingPaymentStepSchema>;

export const VehicleFinancingScopeModeSchema = z.union([
  z.literal('ALL_VEHICLES'),
  z.literal('BRANCH'),
  z.literal('SEGMENT'),
]);
export type VehicleFinancingScopeMode = z.infer<typeof VehicleFinancingScopeModeSchema>;

// Term catalogue (months) shared by `rateMatrix.availableTerms` and each cell's `term`.
export const VehicleFinancingTermSchema = z.union([
  z.literal(12),
  z.literal(18),
  z.literal(24),
  z.literal(30),
  z.literal(36),
  z.literal(42),
  z.literal(48),
  z.literal(54),
  z.literal(60),
  z.literal(66),
  z.literal(72),
  z.literal(78),
  z.literal(84),
  z.literal(90),
  z.literal(96),
]);
export type VehicleFinancingTerm = z.infer<typeof VehicleFinancingTermSchema>;

export const VehicleFinancingDownPaymentSchema = z.union([
  z.literal(0),
  z.literal(10),
  z.literal(20),
  z.literal(30),
  z.literal(40),
  z.literal(50),
  z.literal(60),
  z.literal(70),
  z.literal(80),
  z.literal(90),
]);
export type VehicleFinancingDownPayment = z.infer<typeof VehicleFinancingDownPaymentSchema>;

// Used for both the cell's `balloonPayment` and the template's `defaultBalloonPayment`.
export const VehicleFinancingBalloonPaymentSchema = z.union([
  z.literal(10),
  z.literal(20),
  z.literal(30),
  z.literal(40),
  z.literal(50),
  z.literal(60),
]);
export type VehicleFinancingBalloonPayment = z.infer<typeof VehicleFinancingBalloonPaymentSchema>;

// `IncludableCostRequestBody` / `OneTimeCostsRequestBody` — nested one-time-cost shape used
// ONLY on write (create/update). The read (detail) contract flattens these fields directly
// onto `VehicleFinancingTemplateResponseBody` (see below) — this is a deliberate wire-contract
// asymmetry confirmed against the OpenAPI spec, not something to normalize at this layer.
export const IncludableCostRequestBodySchema = z.object({
  included: z.boolean(),
  amount: NonNegativeMoneySchema.nullable(),
});
export type IncludableCostRequestBody = z.infer<typeof IncludableCostRequestBodySchema>;

export const OneTimeCostsRequestBodySchema = z.object({
  processingFee: NonNegativeMoneySchema.nullable(),
  vehicleTransfer: IncludableCostRequestBodySchema,
  vehicleRegistration: IncludableCostRequestBodySchema,
  vehicleDelivery: IncludableCostRequestBodySchema,
});
export type OneTimeCostsRequestBody = z.infer<typeof OneTimeCostsRequestBodySchema>;

export const VehicleSegmentRequestBodySchema = z.object({
  makes: z.array(z.string()),
  models: z.array(z.string()),
  vehicleCategories: z.array(z.string()),
  fuelTypes: z.array(z.string()),
  yearFrom: z.number().int().nonnegative().nullable(),
  yearTo: z.number().int().nonnegative().nullable(),
  mileageFrom: z.number().int().nonnegative().nullable(),
  mileageTo: z.number().int().nonnegative().nullable(),
  sellingPriceFrom: NonNegativeMoneySchema.nullable(),
  sellingPriceTo: NonNegativeMoneySchema.nullable(),
});
export type VehicleSegmentRequestBody = z.infer<typeof VehicleSegmentRequestBodySchema>;

// `VehicleSegmentResponseBody` is field-identical to `VehicleSegmentRequestBody` per the spec —
// aliased (not merged) so the two can diverge safely, matching the warranty blueprint's
// `SalesWarrantySettingsResponseBodySchema` precedent.
export const VehicleSegmentResponseBodySchema = VehicleSegmentRequestBodySchema;
export type VehicleSegmentResponseBody = z.infer<typeof VehicleSegmentResponseBodySchema>;

export const ScopeRequestBodySchema = z.object({
  mode: VehicleFinancingScopeModeSchema,
  branchIds: z.array(z.string().uuid()),
  segment: VehicleSegmentRequestBodySchema,
});
export type ScopeRequestBody = z.infer<typeof ScopeRequestBodySchema>;

// See `VehicleSegmentResponseBodySchema` comment — same alias rationale applies here.
export const ScopeResponseBodySchema = z.object({
  mode: VehicleFinancingScopeModeSchema,
  branchIds: z.array(z.string().uuid()),
  segment: VehicleSegmentResponseBodySchema,
});
export type ScopeResponseBody = z.infer<typeof ScopeResponseBodySchema>;

export const RateMatrixCellRequestBodySchema = z.object({
  term: VehicleFinancingTermSchema,
  downPayment: VehicleFinancingDownPaymentSchema,
  balloonPayment: VehicleFinancingBalloonPaymentSchema.nullable(),
  nominalRate: NonNegativeBigDecimalSchema,
  apr: NonNegativeBigDecimalSchema,
});
export type RateMatrixCellRequestBody = z.infer<typeof RateMatrixCellRequestBodySchema>;

// Field-identical to `RateMatrixCellRequestBody` per the spec — aliased, see rationale above.
export const RateMatrixCellResponseBodySchema = RateMatrixCellRequestBodySchema;
export type RateMatrixCellResponseBody = z.infer<typeof RateMatrixCellResponseBodySchema>;

export const RateMatrixRequestBodySchema = z.object({
  availableTerms: z.array(VehicleFinancingTermSchema),
  cells: z.array(RateMatrixCellRequestBodySchema),
});
export type RateMatrixRequestBody = z.infer<typeof RateMatrixRequestBodySchema>;

// Field-identical to `RateMatrixRequestBody` per the spec — aliased, see rationale above.
export const RateMatrixResponseBodySchema = RateMatrixRequestBodySchema;
export type RateMatrixResponseBody = z.infer<typeof RateMatrixResponseBodySchema>;

// List response — summary only, per `ListVehicleFinancingTemplateResponseBody` in the spec.
export const ListVehicleFinancingTemplateResponseBodySchema = z.object({
  id: z.string().uuid(),
  name: z.string().max(255),
  type: VehicleFinancingTemplateTypeSchema,
  validFrom: z.string().nullable(),
  validTo: z.string().nullable(),
  archived: z.boolean(),
  eligibleAsAutomatic: z.boolean(),
  automatic: z.boolean(),
  automaticPosition: z.number().int().positive().nullable(),
});
export type ListVehicleFinancingTemplateResponseBody = z.infer<
  typeof ListVehicleFinancingTemplateResponseBodySchema
>;

export const ListVehicleFinancingTemplateApiResponseSchema = z.array(
  ListVehicleFinancingTemplateResponseBodySchema
);
export type ListVehicleFinancingTemplateApiResponse = z.infer<
  typeof ListVehicleFinancingTemplateApiResponseSchema
>;

// Detail response (GET /dms/v1/vehicle-financing-template/{id}). Verified against the
// sales_context OpenAPI spec (`VehicleFinancingTemplateResponseBody`): every field below is
// required by the backend (some are required-but-nullable). One-time costs are FLAT here
// (`vehicleTransferIncluded`, `vehicleTransferAmount`, ...) — see the `OneTimeCostsRequestBody`
// comment above for the write-side nested shape.
export const VehicleFinancingTemplateResponseBodySchema = z.object({
  id: z.string().uuid(),
  name: z.string().max(255),
  validFrom: z.string().nullable(),
  validTo: z.string().nullable(),
  expirationAction: VehicleFinancingTemplateExpirationActionSchema.nullable(),
  replacementTemplateId: z.string().uuid().nullable(),
  type: VehicleFinancingTemplateTypeSchema,
  interestRateType: VehicleFinancingInterestRateTypeSchema.nullable(),
  financialInstitutionCodeId: z.string().uuid(),
  processingFee: NonNegativeMoneySchema.nullable(),
  vehicleTransferIncluded: z.boolean(),
  vehicleTransferAmount: NonNegativeMoneySchema.nullable(),
  vehicleRegistrationIncluded: z.boolean(),
  vehicleRegistrationAmount: NonNegativeMoneySchema.nullable(),
  vehicleDeliveryIncluded: z.boolean(),
  vehicleDeliveryAmount: NonNegativeMoneySchema.nullable(),
  tradeInPossible: z.boolean(),
  creditProtectionAutoEnable: z.boolean(),
  scope: ScopeResponseBodySchema,
  downPaymentStep: VehicleFinancingPaymentStepSchema,
  balloonPaymentStep: VehicleFinancingPaymentStepSchema.nullable(),
  defaultBalloonPayment: VehicleFinancingBalloonPaymentSchema.nullable(),
  // Required-but-nullable, mirroring the create/update contract below: the backend always sends
  // both keys, and either can be `null` while the default option is still unset.
  defaultTerm: VehicleFinancingTermSchema.nullable(),
  defaultDownPayment: VehicleFinancingDownPaymentSchema.nullable(),
  rateMatrix: RateMatrixResponseBodySchema,
  archived: z.boolean(),
  automatic: z.boolean(),
  automaticPosition: z.number().int().positive().nullable(),
});
export type VehicleFinancingTemplateResponseBody = z.infer<
  typeof VehicleFinancingTemplateResponseBodySchema
>;

export type GetVehicleFinancingTemplateApiArg = {
  vehicleFinancingTemplateId: string;
};

// Create/update write contract (`CreateVehicleFinancingTemplateRequestBody` /
// `UpdateVehicleFinancingTemplateRequestBody` in the spec) — field-for-field identical, so the
// update schema is aliased rather than redeclared (same precedent as the response aliases above).
export const CreateVehicleFinancingTemplateRequestBodySchema = z.object({
  name: z.string().max(255),
  validFrom: z.string().nullable(),
  validTo: z.string().nullable(),
  expirationAction: VehicleFinancingTemplateExpirationActionSchema.nullable(),
  replacementTemplateId: z.string().uuid().nullable(),
  type: VehicleFinancingTemplateTypeSchema,
  interestRateType: VehicleFinancingInterestRateTypeSchema.nullable(),
  financialInstitutionCodeId: z.string().uuid(),
  oneTimeCosts: OneTimeCostsRequestBodySchema,
  tradeInPossible: z.boolean(),
  creditProtectionAutoEnable: z.boolean(),
  scope: ScopeRequestBodySchema,
  downPaymentStep: VehicleFinancingPaymentStepSchema,
  balloonPaymentStep: VehicleFinancingPaymentStepSchema.nullable(),
  defaultBalloonPayment: VehicleFinancingBalloonPaymentSchema.nullable(),
  // Required-but-nullable in the backend OpenAPI contract. Omitting either property makes the
  // complete create/update JSON body invalid before domain validation runs.
  defaultTerm: VehicleFinancingTermSchema.nullable(),
  defaultDownPayment: VehicleFinancingDownPaymentSchema.nullable(),
  rateMatrix: RateMatrixRequestBodySchema,
});
export type CreateVehicleFinancingTemplateRequestBody = z.infer<
  typeof CreateVehicleFinancingTemplateRequestBodySchema
>;

export const UpdateVehicleFinancingTemplateRequestBodySchema =
  CreateVehicleFinancingTemplateRequestBodySchema;
export type UpdateVehicleFinancingTemplateRequestBody = z.infer<
  typeof UpdateVehicleFinancingTemplateRequestBodySchema
>;

export type CreateVehicleFinancingTemplateApiArg = {
  createVehicleFinancingTemplateRequestBody: CreateVehicleFinancingTemplateRequestBody;
};

// POST /dms/v1/vehicle-financing-template returns 201 with the new template id
// (`UuidResponseBody` in the spec). Reuse the shared schema rather than redeclaring `{id: string}`.
export type CreateVehicleFinancingTemplateApiResponse = UuidResponseBody;

export type UpdateVehicleFinancingTemplateApiArg = {
  vehicleFinancingTemplateId: string;
  updateVehicleFinancingTemplateRequestBody: UpdateVehicleFinancingTemplateRequestBody;
};

export type ArchiveVehicleFinancingTemplateApiArg = {
  vehicleFinancingTemplateId: string;
  archiveVehicleFinancingTemplateRequestBody: ArchiveVehicleFinancingTemplateRequestBody;
};

export const ArchiveVehicleFinancingTemplateRequestBodySchema = z
  .object({
    action: VehicleFinancingTemplateExpirationActionSchema,
    replacementTemplateId: z.string().uuid().nullable(),
  })
  .superRefine((body, context) => {
    // `REPLACE` moves every vehicle on the archived template onto the replacement, so the
    // replacement is mandatory — archiving without one would silently clear their financing.
    // `replacementTemplateId` is already constrained to a non-empty UUID by the field schema.
    if (body.action === 'REPLACE' && isNil(body.replacementTemplateId)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Replacing on archive requires a replacement template',
        path: ['replacementTemplateId'],
      });
    }
  });
export type ArchiveVehicleFinancingTemplateRequestBody = z.infer<
  typeof ArchiveVehicleFinancingTemplateRequestBodySchema
>;

export type RestoreVehicleFinancingTemplateApiArg = {
  vehicleFinancingTemplateId: string;
};

export type DeleteVehicleFinancingTemplateApiArg = {
  vehicleFinancingTemplateId: string;
};

export type CloneVehicleFinancingTemplateApiArg = {
  vehicleFinancingTemplateId: string;
};

// POST /dms/v1/vehicle-financing-template/{id}/clone returns 201 with the new clone's id
// (`UuidResponseBody` in the spec).
export type CloneVehicleFinancingTemplateApiResponse = UuidResponseBody;

export const AutomaticVehicleFinancingTemplatePositionRequestBodySchema = z.object({
  templateId: z.string().uuid(),
  position: z.number().int().positive(),
});
export type AutomaticVehicleFinancingTemplatePositionRequestBody = z.infer<
  typeof AutomaticVehicleFinancingTemplatePositionRequestBodySchema
>;

export const ReorderAutomaticVehicleFinancingTemplatesRequestBodySchema = z.object({
  type: VehicleFinancingTemplateTypeSchema,
  positions: z.array(AutomaticVehicleFinancingTemplatePositionRequestBodySchema),
});
export type ReorderAutomaticVehicleFinancingTemplatesRequestBody = z.infer<
  typeof ReorderAutomaticVehicleFinancingTemplatesRequestBodySchema
>;

export type ReorderAutomaticVehicleFinancingTemplatesApiArg = {
  reorderAutomaticVehicleFinancingTemplatesRequestBody: ReorderAutomaticVehicleFinancingTemplatesRequestBody;
};
